import { useContext, useMemo } from "react";
import { Modal, Flex, Button, Typography, Space, theme, Empty, Divider } from "antd";
import { ThunderboltOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import ReactGA from "react-ga4";
import QLayoutContext from "contexts/Layout";
import type { ItemData } from "types/layout";
import { iconMap, labelMap, quickCreateLabelMap } from "tokens/layout";
import GAMap from "tokens/GAMap.json";

const { useToken } = theme;
const { Title, Text } = Typography;

interface QuickCreateAction {
  id: string;
  link: string;
}

interface QuickCreateGroup {
  id: string;
  actions: QuickCreateAction[];
}

interface QuickCreateModalProps {
  open: boolean;
  onClose: () => void;
}

const collectCreatable = (item: ItemData): QuickCreateAction[] => {
  const actions: QuickCreateAction[] = [];

  if (item?.props?.id && item.new_entity?.allowed && item.new_entity.link) {
    actions.push({ id: item.props.id, link: item.new_entity.link });
  }

  item?.children?.forEach((child) => {
    actions.push(...collectCreatable(child));
  });

  return actions;
};

export const hasQuickCreateActions = (data?: ItemData[]): boolean =>
  (data ?? []).some((module) => module?.props?.id && collectCreatable(module).length > 0);

const QuickCreateModal = ({ open, onClose }: QuickCreateModalProps) => {
  const { t } = useTranslation();
  const { token } = useToken();
  const { sideMenuData } = useContext(QLayoutContext);

  const groups = useMemo<QuickCreateGroup[]>(
    () =>
      (sideMenuData ?? [])
        .filter((module) => module?.props?.id)
        .map((module) => ({
          id: module.props.id,
          actions: collectCreatable(module),
        }))
        .filter((group) => group.actions.length > 0),
    [sideMenuData],
  );

  const handleActionClick = () => {
    ReactGA.event({
      category: GAMap.sidemenu.CATEGORY,
      action: GAMap.sidemenu.quickCreate.create,
    });
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={680}
      title={
        <Space align="start">
          <ThunderboltOutlined style={{ fontSize: 22, color: token.colorPrimary }} />
          <Space direction="vertical" size={0}>
            <Title level={5} style={{ margin: 0 }}>
              {t("activerecord.attributes.layout.quick_create_title")}
            </Title>
            <Text type="secondary" strong>
              {t("activerecord.attributes.layout.quick_create_subtitle")}
            </Text>
          </Space>
        </Space>
      }
    >
      <Divider style={{ marginTop: 0, marginBottom: token.marginSM }} />
      {groups.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <Space
          direction="vertical"
          size="middle"
          style={{
            width: "100%",
            maxHeight: "60vh",
            overflowY: "auto",
            paddingInlineEnd: token.paddingSM,
          }}
        >
          {groups.map((group) => (
            <div key={group.id}>
              <Text type="secondary" strong>
                {t(labelMap.get(group.id)!)}
              </Text>
              <Flex wrap gap={12} style={{ marginTop: token.marginXS }}>
                {group.actions.map((action) => (
                  <Button
                    key={action.id}
                    href={action.link}
                    onClick={handleActionClick}
                    icon={iconMap.get(action.id)}
                    style={{
                      flex: "0 1 calc(50% - 6px)",
                      minWidth: 0,
                      height: "auto",
                      padding: token.paddingSM,
                      textAlign: "start",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-start",
                      gap: token.marginXS,
                    }}
                  >
                    {t((quickCreateLabelMap.get(action.id) ?? labelMap.get(action.id))!)}
                  </Button>
                ))}
              </Flex>
            </div>
          ))}
        </Space>
      )}
    </Modal>
  );
};

export default QuickCreateModal;
